var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React from 'react';
import { hasValue, toStr, trimSlash } from '@utils';
import { useUXPContext } from '@core/context/useUXPContext';
const PIPE_SERVICE_BASE = 'api/UXP/ExportPipe';
const APPEND_RETRIES = 3;
// grace before removing the download iframe after completion: the controller
// may still be draining the last chunks to the browser after Close is acked
const FRAME_CLEANUP_DELAY_MS = 60 * 1000;
class ExportServerUnavailableError extends Error {
    constructor() { super('Export pipe endpoint unavailable'); this.name = 'ExportServerUnavailableError'; }
}
class ExportAbortError extends DOMException {
    constructor() { super('Export aborted', 'AbortError'); }
}
const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));
// ---- CSV assembly ----
const csvQuote = (value) => `"${value.replace(/"/g, '""')}"`;
const formatValue = (value, row, column) => {
    if (column.formatter)
        return column.formatter(value, row);
    if (!hasValue(value))
        return '';
    return toStr(value);
};
const buildHeaderChunk = (columns) => {
    const headerLine = columns.map(col => csvQuote(col.label || col.id)).join(',');
    return '﻿' + headerLine + '\n';
};
const buildRowsChunk = (items, columns) => {
    const lines = items.map(row => columns.map(col => csvQuote(String(formatValue(row[col.id], row, col)))).join(','));
    return lines.join('\n') + '\n';
};
// ---- engine state ----
let engineContext = null;
const activeRuns = new Map();
const listeners = new Set();
let unloadHandlersInstalled = false;
const onBeforeUnload = (e) => {
    // an export is fed by this tab's JS - a reload/close kills it. Ask the
    // user to confirm (browsers show a generic message; the in-app banner
    // carries the explicit "export will be cancelled" wording).
    e.preventDefault();
    e.returnValue = '';
    return '';
};
const onPageHide = () => {
    // the page is going away (reload/close/navigate). Tell the server to abort
    // every running pipe so the orphaned download fails fast instead of the
    // browser hanging on "Resuming...". sendBeacon is the reliable unload-time
    // transport; apikey rides in the query since we can't set headers here.
    const ctx = engineContext;
    if (!ctx || activeRuns.size === 0)
        return;
    const url = `${trimSlash(ctx.lucyUrl)}/${PIPE_SERVICE_BASE}Abort?apikey=${encodeURIComponent(ctx.apiKey)}`;
    activeRuns.forEach(run => {
        try {
            navigator.sendBeacon(url, new URLSearchParams({ id: run.id }));
        }
        catch ( /* best-effort */_a) { /* best-effort */ }
    });
};
const notifyActivity = () => {
    const active = activeRuns.size > 0;
    if (active && !unloadHandlersInstalled) {
        window.addEventListener('beforeunload', onBeforeUnload);
        window.addEventListener('pagehide', onPageHide);
        unloadHandlersInstalled = true;
    }
    else if (!active && unloadHandlersInstalled) {
        window.removeEventListener('beforeunload', onBeforeUnload);
        window.removeEventListener('pagehide', onPageHide);
        unloadHandlersInstalled = false;
    }
    listeners.forEach(fn => fn());
};
// ---- pipe transport ----
const callPipeService = (context, name, params) => __awaiter(void 0, void 0, void 0, function* () {
    // urlencoded, NOT FormData: multipart text fields get their newlines
    // normalized to CRLF by the browser; percent-encoding round-trips the
    // chunk byte-exact
    return context.executeAPISafe({
        url: `${PIPE_SERVICE_BASE}${name}`,
        method: 'post',
        data: new URLSearchParams(params)
    });
});
/**
 * Idempotent append: retried on transport errors - the server ignores a seq
 * it has already applied, so a retry after a lost ack can never duplicate rows.
 */
const appendChunk = (context, id, seq, content) => __awaiter(void 0, void 0, void 0, function* () {
    let lastError = null;
    for (let attempt = 0; attempt < APPEND_RETRIES; attempt++) {
        if (attempt > 0)
            yield sleep(1000 * attempt);
        const { error } = yield callPipeService(context, 'Append', { id, seq: String(seq), content });
        if (!error)
            return;
        lastError = error;
        const message = toStr(error).toLowerCase();
        if (message.includes('aborted'))
            throw new ExportAbortError();
        if (message.includes('expired') || message.includes('closed') || message.includes('gap'))
            break;
    }
    throw new Error(toStr(lastError) || 'Export append failed');
});
const triggerDownload = (context, id, fileName) => {
    // hidden iframe, NOT a page navigation: navigating would fire beforeunload
    // handlers on click and a new tab would hit popup blockers. The iframe
    // request can't carry the APIKEY header - the unguessable pipe GUID is the
    // credential (see UxpExportPipeController)
    const url = `${trimSlash(context.lucyUrl)}/uxpexport/${id}`
        + `?filename=${encodeURIComponent(fileName + '.csv')}`;
    const frame = document.createElement('iframe');
    frame.style.display = 'none';
    frame.src = url;
    document.body.appendChild(frame);
    return frame;
};
const pump = (context, options) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const { id, fileName, columns, pageSize, totalRows, fetchPage, onProgress } = options;
    const run = { id, fileName, aborted: false };
    activeRuns.set(id, run);
    notifyActivity();
    let seq = -1;
    let page = 1;
    let processedRows = 0;
    const report = (isExporting, progressOverride) => {
        const progress = progressOverride !== null && progressOverride !== void 0 ? progressOverride : (totalRows > 0 ? Math.min((processedRows / totalRows) * 100, 100) : 0);
        onProgress === null || onProgress === void 0 ? void 0 : onProgress({ isExporting, progress, currentPage: page, totalRows, processedRows });
    };
    const checkAbort = () => { if (run.aborted)
        throw new ExportAbortError(); };
    try {
        // header first: this creates the pipe (and doubles as server
        // capability detection), then the download can be triggered
        yield appendChunk(context, id, 0, buildHeaderChunk(columns)).catch(error => {
            if (error instanceof ExportAbortError)
                throw error;
            throw new ExportServerUnavailableError();
        });
        seq = 0;
        run.frame = triggerDownload(context, id, fileName);
        report(true);
        while (true) {
            checkAbort();
            const result = yield fetchPage(page);
            checkAbort();
            const items = (result === null || result === void 0 ? void 0 : result.items) || [];
            if (items.length === 0)
                break;
            yield appendChunk(context, id, page, buildRowsChunk(items, columns));
            seq = page;
            page += 1;
            processedRows += items.length;
            report(true);
            if (items.length < pageSize)
                break; // short page = last page
        }
        // close must land - a lost close would leave the pipe to idle out and
        // fail the download after we reported success
        let closeError = null;
        for (let attempt = 0; attempt < APPEND_RETRIES; attempt++) {
            if (attempt > 0)
                yield sleep(1000 * attempt);
            const { error } = yield callPipeService(context, 'Close', { id, seq: String(seq) });
            closeError = error;
            if (!error)
                break;
        }
        if (closeError)
            throw new Error(toStr(closeError));
        report(false, 100);
        const frame = run.frame;
        if (frame)
            setTimeout(() => frame.remove(), FRAME_CLEANUP_DELAY_MS);
    }
    catch (error) {
        // fire-and-forget: tell the server so the download fails visibly
        callPipeService(context, 'Abort', { id }).catch(() => null);
        report(false, 0);
        (_a = run.frame) === null || _a === void 0 ? void 0 : _a.remove();
        throw error;
    }
    finally {
        activeRuns.delete(id);
        notifyActivity();
    }
});
export const exportEngine = {
    /** keep the engine pointed at the latest context (cheap, called on provider render) */
    configure(context) {
        engineContext = context;
    },
    /**
     * Start a new export. Resolves when the export completes; rejects with
     * AbortError / ExportServerUnavailableError / Error so callers can surface
     * the right message.
     */
    start(options) {
        return __awaiter(this, void 0, void 0, function* () {
            const context = engineContext;
            if (!context)
                throw new Error('Export engine not initialised');
            yield pump(context, options);
        });
    },
    abort(id) {
        const run = activeRuns.get(id);
        if (run)
            run.aborted = true;
    },
    isRunning(id) {
        return activeRuns.has(id);
    },
    /** subscribe to active-export changes (for the "export in progress" banner) */
    subscribe(listener) {
        listeners.add(listener);
        return () => { listeners.delete(listener); };
    },
    getActive() {
        return Array.from(activeRuns.values()).map(run => ({ id: run.id, fileName: run.fileName }));
    }
};
/**
 * Live list of running exports. Drives the "export in progress" banner.
 */
export const useExportActivity = () => {
    const [active, setActive] = React.useState(() => exportEngine.getActive());
    React.useEffect(() => {
        const update = () => setActive(exportEngine.getActive());
        update();
        return exportEngine.subscribe(update);
    }, []);
    return active;
};
/**
 * Mounted once by UXPContextWrapper: keeps the engine's context reference fresh.
 */
export const ExportEngineBootstrap = () => {
    const context = useUXPContext();
    React.useEffect(() => {
        exportEngine.configure(context);
    }, [context]);
    return null;
};
